import { useEffect, useState } from "react";
import { Loader2, Mail, User, Clock, MessageSquare, Search, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";
import { useLanguage } from "@/context/LanguageContext";
import { useAdminBadges } from "@/context/AdminBadgeContext";
import api, { apiError } from "@/lib/api";

export default function AdminMessages() {
  const { lang } = useLanguage();
  const es = lang === "es";
  const { markMessagesSeen } = useAdminBadges();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/messages").then((r) => setMessages(r.data.messages)).finally(() => setLoading(false));
  };
  useEffect(load, []);
  useEffect(() => { markMessagesSeen(); }, [markMessagesSeen]);

  const doDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    try {
      await api.delete(`/admin/messages/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success(es ? "Mensaje eliminado" : "Message deleted");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const filtered = messages.filter((m) => {
    const q = search.toLowerCase();
    return !q || (m.name || "").toLowerCase().includes(q) || (m.email || "").toLowerCase().includes(q) ||
      (m.subject || "").toLowerCase().includes(q) || (m.message || "").toLowerCase().includes(q);
  });

  return (
    <div data-testid="admin-messages">
      <div className="mb-8">
        <h1 className="font-serif text-4xl font-light">{es ? "Mensajes" : "Messages"}</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {messages.length} {es ? "mensajes recibidos desde el formulario de contacto" : "messages received from the contact form"}
        </p>
      </div>

      <div className="relative mb-5 max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={es ? "Buscar por nombre / correo / asunto" : "Search by name / email / subject"} className="rounded-none pl-9" data-testid="message-search" />
      </div>

      {loading ? (
        <div className="py-24 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center text-muted-foreground border border-border bg-card" data-testid="messages-empty">
          <MessageSquare className="h-8 w-8 mx-auto mb-3 opacity-40" />
          {es ? "Aún no hay mensajes." : "No messages yet."}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {filtered.map((m) => (
            <div key={m.id} className="border border-border bg-card p-6 flex flex-col" data-testid={`message-card-${m.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium flex items-center gap-1.5" data-testid={`message-name-${m.id}`}><User className="h-3.5 w-3.5 text-gold shrink-0" /> {m.name}</p>
                  <a href={`mailto:${m.email}`} className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1 hover:text-gold break-all" data-testid={`message-email-${m.id}`}>
                    <Mail className="h-3.5 w-3.5 text-gold shrink-0" /> {m.email}
                  </a>
                </div>
                <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0" data-testid={`message-date-${m.id}`}>
                  <Clock className="h-3 w-3" /> {formatDateTime(m.created_at)}
                </span>
              </div>

              {m.subject ? (
                <p className="overline text-gold mt-4">{es ? "Asunto" : "Subject"}: <span className="text-foreground normal-case tracking-normal font-medium">{m.subject}</span></p>
              ) : null}

              <p className="text-sm text-muted-foreground mt-3 leading-relaxed whitespace-pre-wrap flex-1" data-testid={`message-text-${m.id}`}>{m.message}</p>

              <div className="flex justify-end mt-5 pt-4 border-t border-border">
                <Button size="sm" variant="ghost" className="rounded-none hover:text-destructive" onClick={() => setDeleteId(m.id)} data-testid={`delete-message-${m.id}`}>
                  <Trash2 className="h-4 w-4 mr-1" /> {es ? "Eliminar" : "Delete"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>{es ? "¿Eliminar este mensaje?" : "Delete this message?"}</AlertDialogTitle>
            <AlertDialogDescription>{es ? "Esta acción no se puede deshacer." : "This action cannot be undone."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">{es ? "Cancelar" : "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-message">{es ? "Eliminar" : "Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
